import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { User } from '@/types';

/**
 * 사용자 관리 공통 상태 Context
 * 다이얼로그 상태와 알림 상태를 관리합니다.
 */
interface UserManagementContextType {
  // 다이얼로그 상태
  editingUser: User | null;
  setEditingUser: (user: User | null) => void;
  originalUser: User | null;
  setOriginalUser: (user: User | null) => void;
  passwordChangeUser: User | null;
  setPasswordChangeUser: (user: User | null) => void;
  authTypeChangeUser: User | null;
  setAuthTypeChangeUser: (user: User | null) => void;
  
  // 알림 상태
  alertOpen: boolean;
  setAlertOpen: (open: boolean) => void;
  alertMessage: string;
  setAlertMessage: (message: string) => void;
  alertTitle: string;
  setAlertTitle: (title: string) => void;
  showAlert: (title: string, message: string) => void;
}

const UserManagementContext = createContext<UserManagementContextType | undefined>(undefined);

export const useUserManagementContext = () => {
  const context = useContext(UserManagementContext);
  if (context === undefined) {
    throw new Error('useUserManagementContext must be used within a UserManagementProvider');
  }
  return context;
};

interface UserManagementProviderProps {
  children: ReactNode;
}

export const UserManagementProvider: React.FC<UserManagementProviderProps> = ({ children }) => {
  // 다이얼로그 상태
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [originalUser, setOriginalUser] = useState<User | null>(null);
  const [passwordChangeUser, setPasswordChangeUser] = useState<User | null>(null);
  const [authTypeChangeUser, setAuthTypeChangeUser] = useState<User | null>(null);
  
  // 알림 상태
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertTitle, setAlertTitle] = useState('');
  
  // 알림 표시 헬퍼
  const showAlert = useCallback((title: string, message: string) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertOpen(true);
  }, []);
  
  const value: UserManagementContextType = {
    editingUser,
    setEditingUser,
    originalUser,
    setOriginalUser,
    passwordChangeUser,
    setPasswordChangeUser,
    authTypeChangeUser,
    setAuthTypeChangeUser,
    alertOpen,
    setAlertOpen,
    alertMessage,
    setAlertMessage,
    alertTitle,
    setAlertTitle,
    showAlert,
  };
  
  return (
    <UserManagementContext.Provider value={value}>
      {children}
    </UserManagementContext.Provider>
  );
};

